import type { ScheduledShow } from '@shady-pines/db';
import { getMinutesSinceStartOfWeek, isNonNil } from '@shady-pines/types';
import { head, isEmpty, isNil } from 'lodash';
import { useEffect } from 'react';
import { useRefreshEntity } from '../use-refresh-entity';
import { useUserStore } from '../user/user-store';
import { useShowStore } from './show-store';

const getUpcomingShows = (shows: ScheduledShow[] | null) => {
  const nowMinutes = getMinutesSinceStartOfWeek();
  return (shows ?? [])
    .sort((a, b) => a.start_time - b.start_time)
    .filter((a) => a.start_time > nowMinutes)
    .slice(0, 10);
};

const getCurrentlyAiring = (shows: ScheduledShow[] | null) => {
  const nowMinutes = getMinutesSinceStartOfWeek();
  const regular = shows?.find((s) => s.start_time <= nowMinutes && s.end_time > nowMinutes);
  if (isNonNil(regular)) {
    return regular;
  }
  const maybeSunday = shows?.find(
    (s) => s.day_of_week === 'sun' && s.start_time <= nowMinutes && s.end_time < s.start_time,
  );
  return maybeSunday;
};

export const useManageShows = () => {
  const setCurrentlyAiringShow = useShowStore((s) => s.setCurrentlyAiringShow);
  const setUpcomingShows = useShowStore((s) => s.setUpcomingShows);
  const currentlyAiringShow = useShowStore((s) => s.currentlyAiringShow);
  const upcomingShows = useShowStore((s) => s.upcomingShows);

  const shows = useShowStore((s) => s.shows);
  const showsFetching = useShowStore((s) => s.loading.shows);
  const lastShowRefresh = useShowStore((s) => s.lastShowRefresh);
  const fetchShows = useShowStore((s) => s.fetchShows);
  const refreshShows = useShowStore((s) => s.refreshShows);

  useRefreshEntity('show', refreshShows, fetchShows);

  useEffect(() => {
    if (showsFetching) {
      return;
    }

    if (isNil(lastShowRefresh) || isEmpty(shows)) {
      void fetchShows();
      return;
    }
  }, [fetchShows, lastShowRefresh, shows, showsFetching]);

  const fetchUserSubscriptions = useShowStore((s) => s.fetchShowSubscriptions);
  const userSubscriptionsFetching = useShowStore((s) => s.loading.subscriptions);
  const subscriptionsFetched = useShowStore((s) => isNonNil(s.showSubscriptionsById));
  const userId = useUserStore((s) => s.me);

  useEffect(() => {
    if (userSubscriptionsFetching || subscriptionsFetched) {
      return;
    }
    console.log('refetching after user id change', userId);
    void fetchUserSubscriptions(userId);
  }, [fetchUserSubscriptions, subscriptionsFetched, userId, userSubscriptionsFetching]);

  useEffect(() => {
    setCurrentlyAiringShow(getCurrentlyAiring(shows) ?? null);
  }, [setCurrentlyAiringShow, shows]);

  useEffect(() => {
    const interval = setInterval(() => {
      const maybeNew = getCurrentlyAiring(shows);
      if (maybeNew?.id !== currentlyAiringShow?.id) {
        setCurrentlyAiringShow(maybeNew ?? null);
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [currentlyAiringShow?.id, setCurrentlyAiringShow, shows]);

  useEffect(() => {
    if (isNil(shows)) {
      return;
    }
    setCurrentlyAiringShow(getCurrentlyAiring(shows) ?? null);
  }, [setCurrentlyAiringShow, shows]);

  useEffect(() => {
    const interval = setInterval(() => {
      const maybeNew = getCurrentlyAiring(shows);
      if (maybeNew?.id !== currentlyAiringShow?.id) {
        setCurrentlyAiringShow(maybeNew ?? null);
      }
    }, 5_000);

    return () => clearInterval(interval);
  }, [currentlyAiringShow?.id, setCurrentlyAiringShow, shows]);

  useEffect(() => {
    if (isNil(shows)) {
      return;
    }
    setUpcomingShows(getUpcomingShows(shows));
  }, [shows, setUpcomingShows]);

  useEffect(() => {
    const interval = setInterval(() => {
      const maybeNew = getUpcomingShows(shows);
      const nextNewUpcomingShow = head(maybeNew);
      const nextOldUpcomingShow = head(upcomingShows);
      if (isNil(nextOldUpcomingShow)) {
        setUpcomingShows(maybeNew);
        return;
      }
      if (nextNewUpcomingShow?.id !== nextOldUpcomingShow.id) {
        console.log('upcoming shows changed!', {
          old: nextOldUpcomingShow.name,
          new: nextNewUpcomingShow?.name,
        });
        setUpcomingShows(maybeNew);
      }
    }, 5_000);

    return () => clearInterval(interval);
  }, [currentlyAiringShow?.id, setUpcomingShows, shows, upcomingShows]);

  const showsFetched = isNonNil(shows);
  const initialLoading = useShowStore((s) => s.loading.initial);
  const setInitialLoadCompleted = useShowStore((s) => s.setInitialLoadCompleted);
  useEffect(() => {
    if (!initialLoading) {
      return;
    }
    if (showsFetched && subscriptionsFetched) {
      setInitialLoadCompleted();
    }
  }, [initialLoading, setInitialLoadCompleted, showsFetched, subscriptionsFetched]);
};
